import React, { useEffect, useMemo, useRef } from 'react'

import { SxProps } from '@mui/material'

import type { TAttributeOptionDto } from '@homepal/api-contract'

import { TMetricView } from 'types/GlobalMetric'
import { TMetricQueryParams, TMetricSourceRef } from 'types/GlobalMetric'
import { WidgetType } from 'types/GlobalWidget'

import { useMetricActions } from 'redux/actions/Metrics'
import { useMetric, useMetricRawData } from 'redux/hooks/Metrics'
import { useTranslation } from 'language/translator'

import { AUTO_SIGNIFICANT_FIGURES, getAutoScale } from 'utils/autoFormat'
import { getLabels, getValues } from 'utils/metric'

import Box from 'components_new/atoms/Box'

import BarChart from 'components_new/molecules/charts/BarChart'

import GenericWidget from 'components_new/organisms/GenericWidget'
import { getPrefixValue } from 'components_new/organisms/Widget/Types/utils'

export interface FilterSegmentParams {
  attributeId: string
  label: string
  value: string
}

interface Props {
  metricView?: TMetricView & { title: string; unit: string | null }
  onFilter?: (params: FilterSegmentParams) => void
  option: TAttributeOptionDto
  // When set, the fetch runs on the stored widget so its and the kpi
  // option's saved filter conditions apply (matching the table view).
  source?: TMetricSourceRef
  sx?: SxProps
}

const CHART_HEIGHT = 300

/**
 * Self-fetching chart for a single suggested segment attribute option.
 * Fetches the current metric segmented by `option` (segment_by = option.id),
 * mirroring how organisms/MetricView fetches segmented metric data. Data is
 * cached in Redux by a query-derived key, so rendering many of these dedups.
 */
const ChartElement = (props: Props) => {
  const { metricView, onFilter, option, source, sx } = props

  const { t, lang } = useTranslation()
  const { tryFetchMetricData } = useMetricActions()
  const metric = useMetric(
    metricView?.metric_id,
    metricView?.variant_kpi_template_id
  )

  const clickedSegmentRef = useRef<number | null>(null)

  const queryParams: TMetricQueryParams = useMemo(() => {
    const filter: TMetricQueryParams['filter'] = {}

    Object.entries(metricView?.filters ?? {}).forEach(([key, values]) => {
      if (values.length > 0) {
        filter[key] = { eq: values }
      }
    })

    return {
      ...(Object.keys(filter).length > 0 ? { filter } : {}),
      from_date: metricView?.from_date ?? undefined,
      to_date: metricView?.to_date ?? undefined,
      period_filter: metricView?.period_filter ?? undefined,
      segment_by: option.id,
      widget_type: WidgetType.BAR_CHART
    }
  }, [metricView, option.id])

  const metricSettings = useMemo(
    () => ({
      rolling_sum: metricView?.rolling_sum ?? null,
      slices: metricView?.slices ?? [],
      variant_kpi_template_id: metricView?.variant_kpi_template_id ?? null,
      // distribution is intentionally omitted: these charts should show the
      // raw row count per segment, not a "per sqm/object/etc." adjusted
      // value.
      distribution: null
    }),
    [metricView]
  )

  const [rawData, rawDataError] = useMetricRawData({
    id: metricView?.metric_id,
    settings: metricSettings,
    queryParams: queryParams,
    source,
    includeKeyFigure: false
  })

  useEffect(() => {
    if (!rawData && !rawDataError && metricView) {
      tryFetchMetricData(
        metricView.metric_id,
        metricSettings,
        queryParams,
        source,
        false
      )
    }
  }, [
    metricView,
    metricSettings,
    queryParams,
    rawData,
    rawDataError,
    source,
    tryFetchMetricData
  ])

  const loading = !rawData && !rawDataError

  const labels = getLabels({ lang, rawData, segmentOption: option })
  const values = getValues({ rawData }, rawDataError)

  // the table keeps the base unit, the chart picks one for its values
  const { unitPrefix, unitLabel } = getAutoScale(
    values,
    metricView?.unit,
    metric?.unit_prefixes
  )

  const handleFilter = () => {
    const idx = clickedSegmentRef.current
    const item = idx !== null ? rawData?.data[idx] : null

    if (idx === null || !item || item.segment === null) return

    onFilter?.({
      attributeId: option.attribute_id,
      label: labels[idx],
      value: item.segment
    })
  }

  return (
    <Box sx={{ height: CHART_HEIGHT, ...sx }}>
      <GenericWidget
        disableFullsize={true}
        disableSubtitle={true}
        editMode={false}
        title={option.name}
      >
        <BarChart
          data={{
            datasets: [
              {
                label: metricView?.title ?? '',
                main: true,
                type: 'bar',
                unit: unitLabel,
                values: values.map((value) => getPrefixValue(value, unitPrefix))
              }
            ],
            labels: labels
          }}
          flexItem={true}
          loading={loading}
          onSegmentClick={(segment) => {
            clickedSegmentRef.current = segment
          }}
          options={[
            {
              iconName: 'Filter' as const,
              onClick: handleFilter,
              title: t('filters', 'add_to_filters')
            }
          ]}
          significantFigures={AUTO_SIGNIFICANT_FIGURES}
          variant="detailed"
        />
      </GenericWidget>
    </Box>
  )
}

export default ChartElement
